import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';

const IndiaMap = () => {
  const [activeCity, setActiveCity] = useState(null);
  const [animatedCities, setAnimatedCities] = useState([]);

  // Major operational cities with approximate positions on India map
  const cities = [
    { id: 1, name: 'Kolkata', state: 'West Bengal', x: 82, y: 52, primary: true },
    { id: 2, name: 'Bengaluru', state: 'Karnataka', x: 55, y: 78 },
    { id: 3, name: 'Mumbai', state: 'Maharashtra', x: 35, y: 55 },
    { id: 4, name: 'Delhi', state: 'NCR', x: 52, y: 25 },
    { id: 5, name: 'Chennai', state: 'Tamil Nadu', x: 60, y: 82 },
    { id: 6, name: 'Hyderabad', state: 'Telangana', x: 52, y: 65 },
    { id: 7, name: 'Ahmedabad', state: 'Gujarat', x: 32, y: 42 },
    { id: 8, name: 'Jaipur', state: 'Rajasthan', x: 42, y: 32 },
    { id: 9, name: 'Lucknow', state: 'Uttar Pradesh', x: 58, y: 32 },
    { id: 10, name: 'Patna', state: 'Bihar', x: 72, y: 38 },
    { id: 11, name: 'Bhubaneswar', state: 'Odisha', x: 72, y: 58 },
    { id: 12, name: 'Guwahati', state: 'Assam', x: 92, y: 35 },
    { id: 13, name: 'Chandigarh', state: 'Punjab', x: 48, y: 20 },
    { id: 14, name: 'Kochi', state: 'Kerala', x: 48, y: 88 },
    { id: 15, name: 'Bhopal', state: 'Madhya Pradesh', x: 48, y: 48 }
  ];

  useEffect(() => {
    // Animate cities appearing one by one
    cities.forEach((city, index) => {
      setTimeout(() => {
        setAnimatedCities(prev => [...prev, city.id]);
      }, index * 150);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="presence" className="india-map-section">
      <div className="map-container">
        {/* Section Header */}
        <div className="section-header">
          <span className="section-tag">Our Presence</span>
          <h2 className="section-title">
            Serving Across
            <span className="gradient-text"> India</span>
          </h2>
          <p className="section-subtitle">
            PAN India operations with dedicated support teams
          </p>
        </div>

        <div className="map-content">
          {/* Map Visual */}
          <div className="map-visual">
            {/* Better India Map SVG */}
            <svg viewBox="0 0 100 100" className="india-outline">
              <defs>
                <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.4" />
                </linearGradient>
                <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#93c5fd" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="mapGlow">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              
              {/* India Map Path - More accurate shape */}
              <path
                className="map-path"
                d="M48 8 L52 7 L56 8 L58 10 L62 9 L66 11 L70 10 L74 12 L78 11 L82 14 L86 13 L90 16 L92 20 L94 24 L95 28 L94 32 L95 36 L94 40 L92 44 L90 48 L88 50 L90 54 L88 58 L86 60 L84 62 L82 58 L80 60 L78 64 L76 68 L74 72 L70 76 L66 80 L62 84 L58 88 L54 90 L50 92 L46 94 L42 92 L40 88 L42 84 L44 80 L42 76 L38 78 L34 76 L32 72 L30 68 L28 64 L26 60 L24 56 L22 52 L20 48 L22 44 L24 40 L26 36 L28 32 L30 28 L32 24 L34 22 L36 20 L38 18 L40 16 L42 14 L44 12 L46 10 L48 8 Z"
                fill="url(#mapGradient)"
                stroke="url(#strokeGradient)"
                strokeWidth="0.8"
                filter="url(#mapGlow)"
              />
              
              {/* Kashmir region */}
              <path
                d="M42 8 L46 6 L50 5 L54 6 L56 8 L54 10 L50 9 L46 10 L44 9 L42 8 Z"
                fill="url(#mapGradient)"
                stroke="url(#strokeGradient)"
                strokeWidth="0.5"
                opacity="0.8"
              />
              
              {/* Northeast region */}
              <path
                d="M90 28 L92 26 L95 28 L97 32 L96 36 L94 38 L92 36 L90 34 L88 32 L90 28 Z"
                fill="url(#mapGradient)"
                stroke="url(#strokeGradient)"
                strokeWidth="0.5"
                opacity="0.8"
              />
            </svg>

            {/* City Markers */}
            {cities.map((city) => (
              <div
                key={city.id}
                className={`city-marker ${animatedCities.includes(city.id) ? 'visible' : ''} ${city.primary ? 'primary' : ''} ${activeCity === city.id ? 'active' : ''}`}
                style={{ left: `${city.x}%`, top: `${city.y}%` }}
                onMouseEnter={() => setActiveCity(city.id)}
                onMouseLeave={() => setActiveCity(null)}
              >
                <div className="marker-pulse"></div>
                <div className="marker-dot"></div>
                {activeCity === city.id && (
                  <div className="city-tooltip">
                    <MapPin size={14} />
                    <span className="city-name">{city.name}</span>
                    <span className="city-state">{city.state}</span>
                  </div>
                )}
              </div>
            ))}

            {/* Connection Lines from Kolkata */}
            <svg className="connection-lines" viewBox="0 0 100 100">
              {cities.slice(1).map((city) => (
                <line
                  key={city.id}
                  x1="82"
                  y1="52"
                  x2={city.x}
                  y2={city.y}
                  className={`connection-line ${animatedCities.includes(city.id) ? 'visible' : ''}`}
                />
              ))}
            </svg>
          </div>

          {/* Map Legend */}
          <div className="map-legend">
            <div className="legend-item">
              <div className="legend-marker primary"></div>
              <span>Headquarters (Kolkata)</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker"></div>
              <span>Operational Cities</span>
            </div>
            <div className="legend-stats">
              <div className="legend-stat">
                <span className="stat-number">20+</span>
                <span className="stat-text">Cities</span>
              </div>
              <div className="legend-stat">
                <span className="stat-number">15+</span>
                <span className="stat-text">States</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndiaMap;
